export interface InsetParams
{
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface ScreenMetrics
{
  width: number;
  height: number;
  stable_height: number;
  safe_area_inset: InsetParams;
  content_safe_area_inset: InsetParams;
}

export class TelegramAPISettings
{
  private constructor() {}
  private static is_initialized = false;

  private static readonly ANDROID_FALLBACK_BOTTOM_INSET: number = 30;
  private static readonly CAPACITOR_ANDROID_FALLBACK_TOP_INSET: number = 32;
  private static readonly TELEGRAM_FALLBACK_TOP_INSET: number = 56;
  private static readonly TELEGRAM_FALLBACK_BOTTOM_INSET: number = 34;
  private static readonly TELEGRAM_FULLSCREEN_CONTROLS_HEIGHT: number = 56;
  private static readonly ANDROID_FALLBACK_SIDE_INSET: number = 0;

  public static async init(): Promise<void>
  {
    if ( this.is_initialized )
    {
      return;
    }
    this.is_initialized = true;

    const webapp: any = window.Telegram?.WebApp;
    if ( !webapp || !this.isTelegramEnvironment() )
    {
      this.lockScreenOrientation();
      this.applySafeAreaCssVariables();
      return;
    }

    if ( typeof webapp.ready === "function" )
    {
      webapp.ready();
    }
    webapp.expand();

    const version = parseFloat( webapp.version || "0" );
    if ( version >= 8 && typeof webapp.requestFullscreen === "function" )
    {
      try
      {
        webapp.requestFullscreen();
        await this.waitForFullscreen( webapp, 1200 );
      }
      catch ( error )
      {
        console.warn( "Telegram fullscreen request failed:", error );
      }
    }


    if ( typeof webapp.lockOrientation === "function" )
    {
      try { webapp.lockOrientation( "portrait" ); }
      catch ( error ) { console.warn( "Telegram orientation lock failed:", error ); }
    }

    this.lockScreenOrientation();

    //for testing
    if ( typeof webapp.onEvent === "function" )
    {
      webapp.onEvent( 'deactivated', () => {
        console.log('Telegram WebApp deactivated - user is closing or switching away');
        console.log('Timestamp:', new Date().toISOString());
      });

      webapp.onEvent( 'activated', () => {
        console.log('Telegram WebApp activated - user returned');
        console.log('Timestamp:', new Date().toISOString());
      });

      webapp.onEvent( 'popupClosed', ( buttonId: any ) => {
        console.log('Telegram popup closed with button:', buttonId);
        console.log('Timestamp:', new Date().toISOString());
      });
    }

    window.addEventListener('beforeunload', () => {
      console.log('beforeunload event triggered - user is trying to close the app');
      console.log('Timestamp:', new Date().toISOString());
    });

    await this.waitForMaximization( webapp, 800 );
    await this.waitForStableViewport( webapp, 1200 );
    this.applySafeAreaCssVariables();
  }

  public static isTelegramEnvironment(): boolean
  {
    const webapp: any = ( window as any )?.Telegram?.WebApp;
    if ( !webapp ) return false;

    const platform = String( webapp.platform || "" ).toLowerCase();
    const isTelegramPlatform = ["android", "ios", "tdesktop", "unigram"].includes( platform );

    const hasInitData = typeof webapp.initData === "string" && webapp.initData.length > 0;
    const hasUnsafeUser = !!webapp.initDataUnsafe?.user?.id;

    return ( hasInitData || hasUnsafeUser ) && isTelegramPlatform;
  }

  public static getUserLanguage(): string | null
  {
    const webapp: any = window.Telegram?.WebApp;
    if ( !webapp || !webapp.initDataUnsafe?.user )
      return null;

    return webapp.initDataUnsafe.user.language_code || null;
  }

  public static getUserId(): string | null
  {
    const webapp: any = window.Telegram?.WebApp;
    const id = webapp?.initDataUnsafe?.user?.id;
    return id ? String( id ) : null;
  }

  private static waitForMaximization( webapp: any, timeout_ms: number ): Promise<void>
  {
    const start_time = Date.now();
    return new Promise( ( resolve ) =>
    {
      const check = () =>
      {
        if ( webapp.isExpanded )
        {
          resolve();
        } else if ( Date.now() - start_time < timeout_ms )
        {
          setTimeout( check, 10 );
        } else
        {
          resolve();
        }
      };
      check();
    } );
  }

  private static waitForStableViewport( webapp: any, timeout: number = 500 ): Promise<void>
  {
    if ( typeof webapp.onEvent !== "function" )
    {
      return Promise.resolve();
    }
    return new Promise( ( resolve ) =>
    {
      let done = false;
      const handler = ( { isStateStable }: any ) =>
      {
        if ( isStateStable && !done )
        {
          done = true;
          webapp.offEvent( "viewportChanged", handler );
          resolve();
        }
      };
      webapp.onEvent( "viewportChanged", handler );
      setTimeout( () =>
      {
        if ( !done )
        {
          done = true;
          webapp.offEvent( "viewportChanged", handler );
          resolve();
        }
      }, timeout );
    } );
  }

  private static waitForFullscreen( webapp: any, timeout_ms: number = 2000 ): Promise<void>
  {
    if ( typeof webapp.onEvent !== "function" )
    {
      return Promise.resolve();
    }
    return new Promise( ( resolve ) =>
    {
      let done = false;
      const handler = ( { is_fullscreen }: any ) =>
      {
        if ( is_fullscreen && !done )
        {
          done = true;
          webapp.offEvent( "fullscreenChanged", handler );
          resolve();
        }
      };
      webapp.onEvent( "fullscreenChanged", handler );
      setTimeout( () =>
      {
        if ( !done )
        {
          done = true;
          webapp.offEvent( "fullscreenChanged", handler );
          resolve();
        }
      }, timeout_ms );
    } );
  }

  public static getScreenMetrics(): ScreenMetrics
  {
    const webapp: any = window.Telegram?.WebApp;
    const fallbackInsets = { top: 0, bottom: 0, left: 0, right: 0 };
    const browser_height = window.visualViewport?.height ?? window.innerHeight;
    const telegram_height = Number( webapp?.viewportHeight );
    const telegram_stable_height = Number( webapp?.viewportStableHeight );
    const metrics: ScreenMetrics = {
      width: window.innerWidth,
      height: Number.isFinite( telegram_height ) && telegram_height > 0
        ? Math.min( telegram_height, browser_height )
        : browser_height,
      stable_height: Number.isFinite( telegram_stable_height ) && telegram_stable_height > 0
        ? Math.min( telegram_stable_height, browser_height )
        : browser_height,
      safe_area_inset: this.normalizeInsets( webapp?.safeAreaInset ?? fallbackInsets ),
      content_safe_area_inset: this.normalizeInsets( webapp?.contentSafeAreaInset ?? fallbackInsets ),
    };
    return metrics;
  }

  public static getSafeAreaInsets(): InsetParams
  {
    const has_mobile_telegram_layout = this.hasMobileTelegramLayout();

    if ( !has_mobile_telegram_layout && this.isCapacitorEnvironment() )
    {
      return this.getCapacitorSafeAreaInsets();
    }

    if ( !has_mobile_telegram_layout && this.isPCEnvironment() )
    {
      return { top: 0, bottom: 0, left: 0, right: 0 };
    }

    const webapp: any = window.Telegram?.WebApp;
    const screen_metrics: ScreenMetrics = this.getScreenMetrics();
    const telegram_version = Number.parseFloat( String( webapp?.version ?? "0" ) );
    const has_telegram_insets = telegram_version >= 8
      && ( this.hasInsetData( webapp?.safeAreaInset )
        || this.hasInsetData( webapp?.contentSafeAreaInset ) );
    const inset_params = this.mergeTelegramInsets( screen_metrics );

    // Some mobile Telegram builds enter fullscreen before publishing a valid
    // contentSafeAreaInset. In that state safeAreaInset protects only the
    // status bar/cutout, while Telegram's Back/menu controls still overlap the
    // canvas below it. Use the fallback only while the content inset is absent
    // or no larger than the system inset; a valid Telegram value stays the
    // authority.
    if ( has_mobile_telegram_layout
      && webapp?.isFullscreen === true
      && screen_metrics.content_safe_area_inset.top <= screen_metrics.safe_area_inset.top )
    {
      const system_top = Math.max(
        screen_metrics.safe_area_inset.top,
        this.TELEGRAM_FALLBACK_TOP_INSET
      );
      inset_params.top = Math.max(
        inset_params.top,
        system_top + this.TELEGRAM_FULLSCREEN_CONTROLS_HEIGHT
      );
    }

    // Bot API < 8 has no safe-area fields. Keep a conservative fallback only
    // for those clients; a reported zero is a valid inset and must not be
    // replaced with an arbitrary 56/34 px padding.
    if ( has_mobile_telegram_layout && !has_telegram_insets )
    {
      inset_params.top = this.TELEGRAM_FALLBACK_TOP_INSET;
      inset_params.bottom = this.TELEGRAM_FALLBACK_BOTTOM_INSET;
    }

    if ( this.isAndroidEnvironment() )
    {
      if ( inset_params.left === 0 )
      {
        inset_params.left = this.ANDROID_FALLBACK_SIDE_INSET;
      }
      if ( inset_params.right === 0 )
      {
        inset_params.right = this.ANDROID_FALLBACK_SIDE_INSET;
      }
    }

    return inset_params;
  }

  private static hasMobileTelegramLayout(): boolean
  {
    const webapp: any = window.Telegram?.WebApp;
    if ( !webapp ) return false;

    const platform = String( webapp.platform || "" ).toLowerCase();
    if ( platform === "android" || platform === "ios" )
      return true;

    let telegram_referrer = false;
    try
    {
      telegram_referrer = /(?:^|\.)t(?:elegram)?\.me$/i.test(
        document.referrer ? new URL( document.referrer ).hostname : ""
      );
    }
    catch {}

    const launch_data =
      ( typeof webapp.initData === "string" && webapp.initData.length > 0 )
      || Boolean( webapp.initDataUnsafe?.user?.id )
      || /tgWebApp(?:Data|Platform|Version)/i.test( location.href )
      || telegram_referrer;

    const mobile_device = /android|iphone|ipad|ipod|mobile/i.test( navigator.userAgent );
    return launch_data && mobile_device;
  }

  public static applySafeAreaCssVariables(): void
  {
    const insets = this.getSafeAreaInsets();
    const style = document.documentElement.style;
    style.setProperty( "--tg-safe-top", `${insets.top}px` );
    style.setProperty( "--tg-safe-bottom", `${insets.bottom}px` );
    style.setProperty( "--tg-safe-left", `${insets.left}px` );
    style.setProperty( "--tg-safe-right", `${insets.right}px` );
  }

  public static subscribeToLayoutChanges( callback: () => void ): void
  {
    const update = () =>
    {
      this.applySafeAreaCssVariables();
      callback();
    };
    const webapp: any = window.Telegram?.WebApp;
    if ( typeof webapp?.onEvent === "function" )
    {
      webapp.onEvent( "viewportChanged", update );
      webapp.onEvent( "safeAreaChanged", update );
      webapp.onEvent( "contentSafeAreaChanged", update );
      webapp.onEvent( "fullscreenChanged", update );
      webapp.onEvent( "fullscreenFailed", update );
    }
    window.addEventListener( "resize", update );
    // Safari's collapsing toolbar changes the visual viewport without always
    // firing a window resize.
    window.visualViewport?.addEventListener( "resize", update );

    // A few Telegram Android builds update the WebApp fields silently without
    // emitting the corresponding SDK event. Re-read them through the startup
    // transition so an already-created screen still receives the final values.
    for ( const delay of [ 0, 100, 300, 800, 1500 ] )
      window.setTimeout( update, delay );
  }

  private static getTelegramSafeAreaInsets(): InsetParams
  {
    const screen_metrics: ScreenMetrics = this.getScreenMetrics();
    const inset_params = this.mergeTelegramInsets( screen_metrics );

    if ( this.isAndroidEnvironment() )
    {
      if ( inset_params.bottom === 0 )
      {
        inset_params.bottom = this.ANDROID_FALLBACK_BOTTOM_INSET;
      }
      if ( inset_params.left === 0 )
      {
        inset_params.left = this.ANDROID_FALLBACK_SIDE_INSET;
      }
      if ( inset_params.right === 0 )
      {
        inset_params.right = this.ANDROID_FALLBACK_SIDE_INSET;
      }
    }

    return inset_params;
  }

  private static mergeTelegramInsets( metrics: ScreenMetrics ): InsetParams
  {
    // Both objects describe offsets from the same viewport edge: one protects
    // against system UI, the other against Telegram UI. Taking the stricter
    // value respects both zones without counting overlapping pixels twice.
    return {
      top: Math.max( metrics.safe_area_inset.top, metrics.content_safe_area_inset.top ),
      bottom: Math.max( metrics.safe_area_inset.bottom, metrics.content_safe_area_inset.bottom ),
      left: Math.max( metrics.safe_area_inset.left, metrics.content_safe_area_inset.left ),
      right: Math.max( metrics.safe_area_inset.right, metrics.content_safe_area_inset.right ),
    };
  }

  private static normalizeInsets( value: any ): InsetParams
  {
    const toInset = ( inset: unknown ): number =>
    {
      const number_value = Number( inset );
      return Number.isFinite( number_value ) && number_value > 0
        ? Math.round( number_value )
        : 0;
    };

    return {
      top: toInset( value?.top ),
      bottom: toInset( value?.bottom ),
      left: toInset( value?.left ),
      right: toInset( value?.right ),
    };
  }

  private static hasInsetData( value: any ): boolean
  {
    return value !== null
      && typeof value === "object"
      && [ "top", "bottom", "left", "right" ].some(
        key => Number.isFinite( Number( value[ key ] ) )
      );
  }

  private static getCapacitorSafeAreaInsets(): InsetParams
  {
    const top = this.getCSSEnvValue('safe-area-inset-top');
    const bottom = this.getCSSEnvValue('safe-area-inset-bottom');
    const left = this.getCSSEnvValue('safe-area-inset-left');
    const right = this.getCSSEnvValue('safe-area-inset-right');

    console.log('Capacitor safe areas:', { top, bottom, left, right });

    const capacitor_platform = String(
      ( window as any ).Capacitor?.getPlatform?.() ?? ""
    ).toLowerCase();
    const is_android = capacitor_platform === "android";

    return {
      top: Math.round(
        top || ( is_android ? this.CAPACITOR_ANDROID_FALLBACK_TOP_INSET : 0 )
      ),
      bottom: Math.round(
        bottom || ( is_android ? this.ANDROID_FALLBACK_BOTTOM_INSET : 0 )
      ),
      left: Math.round(left),
      right: Math.round(right),
    };
  }

  private static getCSSEnvValue(variable: string): number
  {
    try {
      const testElement = document.createElement('div');
      testElement.style.position = 'absolute';
      testElement.style.visibility = 'hidden';
      testElement.style.height = `env(${variable})`;
      testElement.style.width = `env(${variable})`;

      document.body.appendChild(testElement);

      const computedStyle = window.getComputedStyle(testElement);
      const heightValue = computedStyle.height;
      const widthValue = computedStyle.width;

      document.body.removeChild(testElement);

      let value = 0;
      if (heightValue && heightValue !== 'auto' && heightValue !== '0px') {
        value = parseFloat(heightValue);
      } else if (widthValue && widthValue !== 'auto' && widthValue !== '0px') {
        value = parseFloat(widthValue);
      }

      return isNaN(value) ? 0 : value;
    } catch (error) {
      console.warn(`Failed to get CSS env(${variable}):`, error);
      return 0;
    }
  }

  private static isCapacitorEnvironment(): boolean
  {
    const capacitor: any = ( window as any ).Capacitor;
    if ( !capacitor || this.hasMobileTelegramLayout() )
      return false;

    const platform = String( capacitor.getPlatform?.() ?? "" ).toLowerCase();
    const native_by_api = capacitor.isNativePlatform?.() === true;
    return native_by_api || platform === "android" || platform === "ios";
  }

  public static isPCEnvironment(): boolean
  {
    const webapp: any = window.Telegram?.WebApp;
    if ( !webapp )
    {
      const userAgent = navigator.userAgent.toLowerCase();
      return !(
        userAgent.includes('android') ||
        userAgent.includes('iphone') ||
        userAgent.includes('ipad') ||
        userAgent.includes('ipod') ||
        userAgent.includes('mobile')
      );
    }

    const platform = webapp.platform || '';
    return platform === 'web' || platform === 'weba' || platform === 'webk' || platform.includes('desk');
  }

  private static isAndroidEnvironment(): boolean
  {
    const webapp: any = window.Telegram?.WebApp;
    if ( !webapp )
    {
      return false;
    }

    const platform = webapp.platform || '';
    return platform === 'android';
  }

  private static lockScreenOrientation(): void
  {
    try {
      if ( this.isCapacitorEnvironment() )
      {
        const { Capacitor } = ( window as any );
        if ( Capacitor?.Plugins?.ScreenOrientation )
        {
          Capacitor.Plugins.ScreenOrientation.lock({ orientation: 'portrait' });
          console.log( 'Capacitor orientation locked to portrait' );
        }
      }
    } catch ( error ) {
      console.warn( 'Error locking screen orientation:', error );
    }
  }

  public static lockOrientation( orientation: "portrait" | "landscape" ): void
  {
    const webapp: any = window.Telegram?.WebApp;
    if ( webapp && typeof webapp.lockOrientation === "function" )
    {
      webapp.lockOrientation( orientation );
    }
  }

  public static unlockOrientation(): void
  {
    const webapp: any = window.Telegram?.WebApp;
    if ( webapp && typeof webapp.unlockOrientation === "function" )
    {
      webapp.unlockOrientation();
    }
  }

  public static debugWebApp( filter_key?: string ): void
  {
    const webapp: any = window.Telegram?.WebApp;
    if ( !webapp )
    {
      console.log( 'Telegram WebApp not available' );
      return;
    }

    console.log( '=== TELEGRAM WEBAPP DEBUG ===' );
    console.log( 'Full WebApp object:', webapp );
    console.log( 'All WebApp keys:', Object.keys( webapp ) );

    if ( filter_key )
    {
      console.log( `\n=== FILTERED BY "${filter_key}" ===` );
      const filtered_keys = Object.keys( webapp ).filter( key =>
        key.toLowerCase().includes( filter_key.toLowerCase() )
      );

      if ( filtered_keys.length > 0 )
      {
        filtered_keys.forEach( key => {
          console.log( `${key}:`, webapp[key] );
        } );
      } else {
        console.log( `No keys found containing "${filter_key}"` );
      }
    }

    console.log( '\n=== INIT DATA ===' );
    console.log( 'initData:', webapp.initData );
    console.log( 'initDataUnsafe:', webapp.initDataUnsafe );

    console.log( '\n=== PLATFORM INFO ===' );
    console.log( 'platform:', webapp.platform );
    console.log( 'version:', webapp.version );
    console.log( '=========================' );
  }
}
